import React, { PropsWithChildren, useContext, useState } from 'react'
import { useAsync } from 'react-async-hook'
import { IntlProvider as ReactIntlProvider } from 'react-intl'

import { PageLoader } from 'component-library-next'

import { logger } from '@app/common/logger/logger'

type IntlContextValue = {
  locale: string
  setLocale: (locale: string) => void
  refetchTranslations: () => void
}

const LocaleContext = React.createContext<IntlContextValue>({} as IntlContextValue)

export type IntlProviderProps = {
  defaultLocale: string
  messages?: Record<string, string>
  fetchTranslations?: (locale: string) => Promise<Record<string, string>>
}

export const IntlProvider = ({
  children,
  defaultLocale = 'de',
  messages,
  fetchTranslations
}: PropsWithChildren<IntlProviderProps>) => {
  const [locale, setLocale] = useState(defaultLocale)
  const { result, loading, execute } = useAsync(() => {
    return typeof fetchTranslations === 'function'
      ? fetchTranslations(locale)
      : Promise.resolve(messages || {})
  }, [locale])

  if (!messages && loading) {
    return <PageLoader loading />
  }

  return (
    <LocaleContext.Provider
      value={{
        locale,
        setLocale,
        refetchTranslations: execute
      }}
    >
      <ReactIntlProvider
        locale={locale}
        messages={result || {}}
        onError={(err) => logger.info(err.message)}
      >
        {children}
      </ReactIntlProvider>
    </LocaleContext.Provider>
  )
}

export const useLocale = () => {
  return useContext(LocaleContext)
}
